import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import {
  CmsForm,
  CastingFormFields,
  CmsPageError,
  NavbarContainer,
  cmsColSpanClass,
} from "@/components";
import { ROUTES } from "@/constants";
import { ApiError, castingsService } from "@/services";
import type { Gender, PaymentType } from "@/types";
import {
  buildCastingContactPayload,
  buildCastingValidationSummary,
  emptyCastingForm,
  type CastingContactField,
  type CastingFormField,
  validateCastingForm,
} from "@/utils/castingForm";
import { CASTINGS_PUBLIC_NEW_LEDE } from "@/data/public/castingsPage";
import "@/pages/backoffice/CmsShared.scss";
import "./CastingPublicNew.scss";

const SUCCESS_MESSAGE =
  "Recibimos tu convocatoria. La revisaremos antes de publicarla.";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

/**
 * Public form for submitting a new casting (pending moderation)
 */
export const CastingPublicNew: React.FC = () => {
  const navigate = useNavigate();
  const turnstileRef = useRef<TurnstileInstance>(null);

  const [form, setForm] = useState(emptyCastingForm("PENDING"));
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<CastingFormField, string>>
  >({});
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactFieldErrors, setContactFieldErrors] = useState<
    Partial<Record<CastingContactField, string>>
  >({});

  const backTo = ROUTES.public.castings;

  const handleSuccessRedirect = () => {
    toast.success(SUCCESS_MESSAGE);
    navigate(backTo);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name in fieldErrors) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    setError(null);
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      contact: { ...prev.contact, [name]: value },
    }));
    if (contactError) {
      setContactError(null);
    }
    if (name in contactFieldErrors) {
      setContactFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (website.trim()) {
      handleSuccessRedirect();
      return;
    }

    const validationResult = validateCastingForm(form);
    const {
      fieldErrors: errors,
      contactError: nextContactError,
      contactFieldErrors: nextContactFieldErrors,
    } = validationResult;

    const hasContactFieldErrors =
      Object.keys(nextContactFieldErrors).length > 0;

    if (
      Object.keys(errors).length > 0 ||
      nextContactError ||
      hasContactFieldErrors
    ) {
      setFieldErrors(errors);
      setContactError(nextContactError);
      setContactFieldErrors(nextContactFieldErrors);
      setError(buildCastingValidationSummary(validationResult));
      return;
    }

    if (!turnstileToken) {
      setError("Completá la verificación de seguridad antes de enviar.");
      return;
    }

    setFieldErrors({});
    setContactError(null);
    setContactFieldErrors({});
    setIsSaving(true);
    setError(null);

    const payload = {
      title: form.title.trim(),
      gender: form.gender,
      fisic: form.fisic.trim(),
      description: form.description.trim(),
      requiredInfo: form.requiredInfo.trim(),
      paymentType: form.paymentType,
      endDate: form.endDate,
      contact: buildCastingContactPayload(form.contact),
      turnstileToken,
    };

    try {
      await castingsService.createPublic(payload);
      handleSuccessRedirect();
    } catch (err) {
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      setError(
        err instanceof ApiError
          ? (err.responseData?.message ?? err.message)
          : "Error al enviar. Intentá de nuevo.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <NavbarContainer title="Agregar casting" backTo={backTo}>
      <p className="casting-public-new__lede">{CASTINGS_PUBLIC_NEW_LEDE}</p>
      <div className="casting-public-new">
        <CmsForm
          backTo={backTo}
          showBackLink={false}
          onSubmit={(e) => void handleSubmit(e)}
          isSaving={isSaving}
          submitLabel="Enviar convocatoria"
          savingLabel="Enviando..."
          submitTrackSuffix="public_casting_submit"
          cancelTrackSuffix="public_casting_cancel"
        >
          <CmsPageError error={error} colSpan={6} />
          <CastingFormFields
            form={form}
            fieldErrors={fieldErrors}
            contactError={contactError}
            contactFieldErrors={contactFieldErrors}
            showReviewStatus={false}
            onFieldChange={handleChange}
            onContactChange={handleContactChange}
            onGenderChange={(value: Gender) => {
              setForm((prev) => ({ ...prev, gender: value }));
              setError(null);
            }}
            onPaymentTypeChange={(value: PaymentType) => {
              setForm((prev) => ({ ...prev, paymentType: value }));
              setError(null);
            }}
          />
          <div className="casting-public-new__honeypot" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          {TURNSTILE_SITE_KEY ? (
            <div className="casting-public-new__turnstile">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ appearance: "interaction-only" }}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>
          ) : (
            <p className={["cms-page__error", cmsColSpanClass(6)].filter(Boolean).join(" ")}>
              Falta configurar la verificación de seguridad (Turnstile).
            </p>
          )}
        </CmsForm>
      </div>
    </NavbarContainer>
  );
}

export default CastingPublicNew;
